import { motion } from "framer-motion";
import { profile } from "../data/mock";

const SectionHeader = ({ eyebrow, title, subtitle, gradient = "aurora" }) => (
  <div className="max-w-3xl">
    <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-cyan-300">
      {eyebrow}
    </p>
    <h2 className={`font-display mt-4 text-4xl sm:text-5xl font-bold tracking-tighter text-balance ${gradient === "aurora" ? "text-white" : "text-white"}`}>
      {title}
    </h2>
    {subtitle && (
      <p className="mt-4 text-cyan-100/70 text-base sm:text-lg leading-relaxed max-w-2xl">
        {subtitle}
      </p>
    )}
  </div>
);

export const About = () => {
  return (
    <section
      id="about"
      className="relative py-24 md:py-32"
      data-testid="about-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        >
          <SectionHeader
            eyebrow="// 01 — About"
            title={
              <>
                Production AI,{" "}
                <span className="text-gradient-aurora animate-gradient-pan font-serif italic">
                  end to end.
                </span>
              </>
            }
            subtitle="What I do, how I think, and what I optimize for."
          />
        </motion.div>

        <div className="mt-16 grid lg:grid-cols-12 gap-10">
          {/* Left — paragraphs */}
          <div className="lg:col-span-8 space-y-6 text-cyan-50/85 text-base sm:text-lg leading-relaxed">
            {profile.about.map((para, i) => (
              <motion.p
                key={i}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{
                  duration: 0.6,
                  delay: i * 0.07,
                  ease: [0.22, 1, 0.36, 1],
                }}
                data-testid={`about-paragraph-${i}`}
              >
                {para}
              </motion.p>
            ))}
          </div>

          {/* Right — focus card */}
          <motion.aside
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="lg:col-span-4"
          >
            <div className="sticky top-24 gradient-border rounded-2xl glass p-6">
              <p className="font-mono text-[11px] uppercase tracking-widest text-rose-300">
                Currently optimizing for
              </p>
              <ul className="mt-5 space-y-3">
                {[
                  ["Latency", "p50 inference under load", "text-cyan-300"],
                  ["Throughput", "tokens/sec on commodity GPUs", "text-cyan-300"],
                  ["Faithfulness", "RAG grounding & NLI verification", "text-pink-300"],
                  ["Cost", "$/1K requests in production", "text-indigo-300"],
                ].map(([k, v, color]) => (
                  <li
                    key={k}
                    className="flex items-start justify-between gap-4 py-2 border-b border-white/5 last:border-b-0"
                  >
                    <span className={`font-display text-sm font-medium ${color}`}>
                      {k}
                    </span>
                    <span className="text-right text-xs text-cyan-200/60 max-w-[55%]">
                      {v}
                    </span>
                  </li>
                ))}
              </ul>

              <div className="mt-6 grid grid-cols-2 gap-3 pt-4 border-t border-white/5">
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-widest text-cyan-300/80">
                    Years
                  </p>
                  <p className="font-display text-2xl text-gradient-aurora font-bold mt-1">
                    3+
                  </p>
                </div>
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-widest text-cyan-300/80">
                    Projects shipped
                  </p>
                  <p className="font-display text-2xl text-gradient-sunset font-bold mt-1">
                    10+
                  </p>
                </div>
              </div>
            </div>
          </motion.aside>
        </div>
      </div>
    </section>
  );
};
